import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Snackbar,
  useMediaQuery
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import api from '../api';
import { useTheme } from '@mui/material/styles';
import ImportCsvEleves from './ImportCsvEleves';

export default function ElevesTable({ token, classe }) {
  const [eleves, setEleves] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nom: '', prenom: '', email: '', classe: classe?._id || '', password: '' });
  const [editId, setEditId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [importOpen, setImportOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchEleves = async () => {
      setLoading(true);
      setError('');
      try {
        const res = classe && classe._id
          ? await api.get(`/eleves?classe=${classe._id}`)
          : await api.get('/eleves');
        if (!Array.isArray(res.data)) {
          setError(typeof res.data === 'object' ? (res.data.error || JSON.stringify(res.data)) : 'Erreur API ou format inattendu');
          setEleves([]);
        } else {
          setEleves(res.data);
        }
      } catch (err) {
        setError(
          err.response?.status === 401
            ? 'Non autorisé : vérifiez votre connexion.'
            : err.response?.data?.error || 'Erreur lors du chargement des élèves.'
        );
        setEleves([]);
      }
      setLoading(false);
    };

    fetchEleves();
  }, [token, classe, open, refreshKey]);

  const handleOpen = (eleve = null) => {
    setOpen(true);
    if (eleve) {
      setForm({
        nom: eleve.nom || '',
        prenom: eleve.prenom || '',
        email: eleve.email || '',
        classe: eleve.classe?._id || (classe ? classe._id : ''),
        password: eleve.password || (eleve.prenom ? eleve.prenom.toLowerCase() + '123' : '')
      });
      setEditId(eleve._id);
    } else {
      setForm({ nom: '', prenom: '', email: '', classe: classe ? classe._id : '', password: '' });
      setEditId(null);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ nom: '', prenom: '', email: '', classe: classe ? classe._id : '', password: '' });
    setEditId(null);
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.nom || !form.prenom || !form.classe) {
      setSnack({ open: true, message: 'Nom, prénom et classe sont requis.', severity: 'error' });
      return;
    }

    const formToSend = {
      nom: form.nom,
      prenom: form.prenom,
      email: form.email,
      classe: form.classe,
      password: form.password || (form.prenom ? form.prenom.toLowerCase() + '123' : '')
    };

    try {
      if (editId) {
        await api.put(`/eleves/${editId}`, formToSend);
        setSnack({ open: true, message: 'Élève modifié.', severity: 'success' });
      } else {
        await api.post('/eleves', formToSend);
        setSnack({ open: true, message: 'Élève ajouté.', severity: 'success' });
      }
      handleClose();
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || err.message || 'Erreur lors de l\'enregistrement.', severity: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cet élève ?')) {
      try {
        await api.delete(`/eleves/${id}`);
        setSnack({ open: true, message: 'Élève supprimé.', severity: 'success' });
        setRefreshKey((k) => k + 1);
      } catch (err) {
        setSnack({ open: true, message: err.response?.data?.error || 'Erreur lors de la suppression.', severity: 'error' });
      }
    }
  };

  if (loading) return <Typography sx={{ mt: 6, textAlign: 'center' }}>Chargement des élèves...</Typography>;
  if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;
  if (!eleves || !Array.isArray(eleves)) return <Alert severity="error" sx={{ m: 4 }}>Problème de données reçues du serveur.</Alert>;

  return (
    <Box sx={{ width: '90%', mt: 4, px: { xs: 0, sm: 2, md: 3 } }}>
      <Paper sx={{ width: '100%', overflowX: 'auto', p: { xs: 1, sm: 2, md: 3 }, boxSizing: 'border-box', maxWidth: '100%', margin: '0 auto', background: '#fafafa' }}>
        <Box
          display="flex"
          flexDirection={isSmall ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems={isSmall ? 'stretch' : 'center'}
          mb={2}
          gap={2}
        >
          <Typography variant={isSmall ? 'h6' : 'h5'}>
            {classe && classe.nom ? `Élèves de la classe ${classe.nom}` : 'Tous les élèves'}
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="contained" onClick={() => handleOpen()} sx={{ minWidth: 150 }}>
              Ajouter un élève
            </Button>
            <Button variant="outlined" onClick={() => setImportOpen(true)} sx={{ minWidth: 150 }}>
              Importer CSV
            </Button>
          </Box>
        </Box>
        <TableContainer sx={{ width: '90%', overflowX: 'auto' }}>
          <Table size={isSmall ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>QR code</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {eleves.map((e) => {
                const password = e.password || (e.prenom ? e.prenom.toLowerCase() + '123' : '');
                const passwordKey = 'password';
                const qrParams = new URLSearchParams({
                  email: e.email || '',
                  [passwordKey]: password
                });
                const qrUrl = `${window.location.origin}/eleve/${e._id}?${qrParams.toString()}`;
                return (
                  <TableRow key={e._id}>
                    <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.nom}</TableCell>
                    <TableCell sx={{ maxWidth: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.prenom}</TableCell>
                    <TableCell sx={{ maxWidth: 220, wordBreak: 'break-all' }}>{e.email || '—'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <a href={qrUrl} target="_blank" rel="noopener noreferrer" title="Ouvrir la page élève" style={{ marginBottom: 4 }}>
                          <QRCodeSVG value={qrUrl} size={isSmall ? 40 : 56} />
                        </a>
                        <a href={qrUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#3f51b5' }}>
                          Ouvrir la page élève
                        </a>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => handleOpen(e)} sx={{ mr: 1 }}>Modifier</Button>
                      <Button size="small" color="error" onClick={() => handleDelete(e._id)}>Supprimer</Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editId ? "Modifier l'élève" : 'Ajouter un élève'}</DialogTitle>
        <DialogContent>
          <form id="eleveForm" onSubmit={handleSubmit}>
            <TextField label="Nom" name="nom" value={form.nom} onChange={handleChange} margin="normal" fullWidth required />
            <TextField label="Prénom" name="prenom" value={form.prenom} onChange={handleChange} margin="normal" fullWidth required />
            <TextField label="Email (optionnel)" name="email" value={form.email} onChange={handleChange} margin="normal" fullWidth />
            <TextField label="Mot de passe (par défaut : prenom123)" name="password" value={form.password || (form.prenom ? form.prenom.toLowerCase() + '123' : '')} onChange={handleChange} margin="normal" fullWidth required />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button type="submit" form="eleveForm" variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importOpen} onClose={() => setImportOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Importer une liste d'élèves (CSV / PRONOTE)</DialogTitle>
        <DialogContent>
          <ImportCsvEleves
            classeId={classe?._id || ''}
            availableClasses={classe ? [classe] : []}
            onImport={() => {
              setImportOpen(false);
              setRefreshKey((k) => k + 1);
            }}
          />
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}